import { useEffect, useState } from 'react';

import {
  Card,
  Divider,
  NativeSelect,
  Skeleton,
  Stack,
  Typography,
  Button,
  Box,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { debounce } from 'lodash';

import { getPatientInfo } from 'apis/admin';
import useI18n from 'hooks/useI18n';
import useNotification from 'hooks/useNotification';
import useStudent from 'store/student/useStudent';
import usePatient from 'store/patient/usePatient';

import PatientInfoItem from './PatientInfoItem';
import useSurvey from 'store/survey/useSurvey';

import TestPerson from 'assets/test-person.svg';

import SmartnurseIcon from 'assets/smartnurse-icon.svg';
interface Props {
  menuDrawerWidth: number;
  setMenuDrawerWidth: (menuDrawerWidth: number) => void;
}

const PatientInfo = (props: Props) => {
  const { menuDrawerWidth, setMenuDrawerWidth } = props;
  const i18n = useI18n();
  const { onFail } = useNotification();
  const { student_name: userName } = useStudent();
  const { onUpdateSurveyType } = useSurvey();

  const { patient, patientInfo, onSelectedPatientInfo } = usePatient();

  const [width, setWidth] = useState(window.innerWidth);

  const handleResize = debounce(() => {
    setWidth(window.innerWidth);
  }, 200);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (width < 1194) {
      setMenuDrawerWidth(52);
    } else {
      setMenuDrawerWidth(300);
    }
  }, [width]);

  // 바코드 모달창 만들기

  const onClickModal = (label: string) => {
    onUpdateSurveyType(label);
  };

  // 부진단 코드 index
  const [diseaseSubIndex, setDiseaseSubIndex] = useState(0);

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientInfo({ patient_id: patient.patient_id })
      .then(({ data }) => {
        onSelectedPatientInfo(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  if (!patientInfo) {
    return <Skeleton variant="rectangular" sx={{ flex: 1 }} />;
  }
  const {
    // column1
    patient_id,
    name,
    age,
    gender,
    blood,
    // column2
    height,
    weight,
    admin_hod,
    admin_pod,
    // column3
    department,
    ward,
    room,
    main_doctor,
    // column4
    disease_main,
    disease_sub,
  } = patientInfo;

  // 부진단 코드 선택 UI
  const DiseaseSubCode = () => (
    <NativeSelect
      value={diseaseSubIndex}
      onChange={e => setDiseaseSubIndex(Number(e.target.value))}
      inputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      sx={{ fontSize: 14, lineHeight: '18px' }}
    >
      {disease_sub?.map(({ disease_id }, index) => (
        <option key={disease_id} value={index}>
          {disease_id}
        </option>
      ))}
    </NativeSelect>
  );

  const convertHeight = (heightStr: string) => {
    // "160cm"에서 숫자만 추출
    const cmValue = parseFloat(heightStr);
    if (isNaN(cmValue)) return heightStr;
    // cm를 인치로 변환
    const totalInches = cmValue / 2.54;
    // 전체 인치에서 피트와 남은 인치 계산
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return `${cmValue}cm (${feet}’${inches}”)`;
  };

  const convertWeight = (weightStr: string) => {
    // "52kg"에서 숫자만 추출
    const kgValue = parseFloat(weightStr);
    if (isNaN(kgValue)) return weightStr;
    // kg를 파운드로 변환
    const pounds = kgValue * 2.20462;
    return `${kgValue}kg (${pounds.toFixed(1)} lbs)`;
  };

  return (
    <div style={{ zIndex: 1000 }}>
      {menuDrawerWidth !== 300 ? (
        <Box
          className="userDiv"
          style={{ lineHeight: '24px', paddingTop: '5px' }}
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          <ArrowForwardIosIcon
            className="hamburgerIcon"
            fontSize="small"
            onClick={() => {
              if (menuDrawerWidth !== 300) setMenuDrawerWidth(300);
              else setMenuDrawerWidth(52);
            }}
            sx={{ marginTop: '0px', color: '#2B308B' }}
          />
          {menuDrawerWidth === 300}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {menuDrawerWidth === 300 ? (
              <Box
                height={48}
                component="img"
                alt={'SmartNurse'}
                src={SmartnurseIcon}
                sx={{ objectFit: 'contain', marginBottom: '20px' }}
              />
            ) : null}
            <ArrowForwardIosIcon
              className="hamburgerIcon"
              fontSize="small"
              onClick={() => {
                if (menuDrawerWidth !== 300) setMenuDrawerWidth(300);
                else setMenuDrawerWidth(52);
              }}
              sx={{ marginTop: '20px', marginLeft: '240px', color: '#2B308B' }}
            />
            {menuDrawerWidth === 300}
          </Box>

          <Card
            component="section"
            sx={{
              p: 2,
              overflow: 'visible',
              justifyContent: 'space-between',
              gap: { xs: 1.25, xl: 2.5 },
              backgroundColor: '#B1CCF5',
            }}
          >
            <Stack spacing={1.25} sx={{ marginBottom: '10px' }}>
              <Box
                height={183}
                component="img"
                alt={'SmartNurse'}
                src={patient?.photo !== null ? patient?.photo : TestPerson}
                sx={{
                  objectFit: 'contain',
                  marginBottom: '20px',
                  borderRadius: '8px',
                }}
              />
            </Stack>
            <Stack spacing={1.25} sx={{ marginBottom: '10px' }}>
              <PatientInfoItem title="登記號碼." content={patient_id} />
              <PatientInfoItem title="姓名" content={name} />
              <PatientInfoItem title="年齡" content={age} />
              {/* <PatientInfoItem
                title="性別"
                content={i18n(`GENDER.${gender}`)}
              /> */}
            </Stack>
            <Divider orientation="vertical" />
            <Stack spacing={1.25} sx={{ margin: '10px 0 10px 0' }}>
              <PatientInfoItem title="血型" content={blood} />
              <PatientInfoItem title="身高" content={convertHeight(height)} />
              <PatientInfoItem title="體重" content={convertWeight(weight)} />
              <PatientInfoItem title="住院日" content={admin_hod} />
              <PatientInfoItem title="術後日" content={admin_pod} />
            </Stack>
            <Divider orientation="vertical" />
            <Stack spacing={1.25} sx={{ margin: '10px 0 10px 0' }}>
              <PatientInfoItem title="科室" content={department} />
              <PatientInfoItem title="主治醫師" content={main_doctor} />
              <PatientInfoItem title="病房" content={ward} />
              <PatientInfoItem title="房間" content={room} />
              <PatientInfoItem title="主管護士" content={userName} />
            </Stack>
            <Divider orientation="vertical" />
            <Stack spacing={1.25} sx={{ margin: '10px 0 10px 0' }}>
              <PatientInfoItem
                title="診斷代碼"
                content={disease_main.disease_id}
              />
              <PatientInfoItem
                title="診斷"
                content={disease_main.disease_eng}
              />
              <PatientInfoItem
                title="診斷代碼"
                content={
                  disease_sub.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'right',
                        width: '140px',
                      }}
                    >
                      <DiseaseSubCode />
                    </Box>
                  ) : null
                }
              />
              <PatientInfoItem
                title="次診斷"
                content={disease_sub[diseaseSubIndex]?.disease_eng}
              />
            </Stack>
          </Card>

          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="small"
              sx={{
                margin: '20px auto 5px auto',
                backgroundColor: '#2D73DC',
                color: 'white',
              }}
              onClick={() => onClickModal('病人标签打印')}
            >
              病人标签打印
            </Button>
          </Box>
        </>
      )}
    </div>
  );
};

export default PatientInfo;
